import React, { useState, useEffect, useRef } from "react";
import { 
  AdvancedFilterDef, 
  AdvancedSortDef, 
  AdvancedState, 
  FilterValue,
  SortParam
} from "./advanced-filter-types";
import { Button } from "@/ui-components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/ui-components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui-components/ui/popover";
import { SlidersHorizontal, Plus, RotateCcw, ArrowDownAZ, Filter } from "lucide-react";
import { Badge } from "@/ui-components/ui/badge";
import { AdvancedFilterRow } from "./advanced-filter-row";
import { AdvancedSortRow } from "./advanced-sort-row";
import { cn } from "@/lib/utils/cn";
import { motion, AnimatePresence } from "framer-motion";

interface AdvancedFilterSortPanelProps {
  filterDefs: AdvancedFilterDef[];
  sortDefs: AdvancedSortDef[];
  state: AdvancedState;
  onChange: (newState: AdvancedState) => void;
  onReset: () => void;
  className?: string;
  defaultSort?: SortParam;
}

export function AdvancedFilterSortPanel({
  filterDefs,
  sortDefs,
  state,
  onChange,
  onReset,
  className,
  defaultSort
}: AdvancedFilterSortPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  const filterIdCounter = useRef(0);
  const [filterIds, setFilterIds] = useState<number[]>(() => 
    state.filters.map(() => filterIdCounter.current++)
  );

  useEffect(() => {
    setFilterIds(prev => {
      if (prev.length === state.filters.length) return prev;
      return state.filters.map((_, i) => prev[i] ?? filterIdCounter.current++);
    });
  }, [state.filters, state.filters.length]);

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = () => {
      setIsDesktop(mql.matches);
      setIsOpen(false);
    };
    mql.addEventListener("change", onChange);
    setIsDesktop(mql.matches);
    
    // Also close on any window resize to prevent floating popovers from hidden triggers
    const onResize = () => setIsOpen(false);
    window.addEventListener("resize", onResize);
    
    return () => {
      mql.removeEventListener("change", onChange);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  
  // Calculate active counts
  const filterCount = state.filters.length;
  const sortCount = state.sorts.filter(s => {
    if (!defaultSort) return true;
    return s.key !== defaultSort.key || s.direction !== defaultSort.direction;
  }).length;
  const totalCount = filterCount + sortCount;

  // Handlers
  const addFilter = () => {
    // Find first available key
    const usedKeys = state.filters.map(f => f.key);
    const availableDef = filterDefs.find(d => !usedKeys.includes(d.key));
    if (availableDef) {
      setFilterIds(prev => [...prev, filterIdCounter.current++]);
      onChange({
        ...state,
        filters: [...state.filters, { key: availableDef.key, value: "" }]
      });
    }
  };

  const updateFilterKey = (index: number, newKey: string) => {
    // When changing key, check if key is already used (except by self)
    const isUsed = state.filters.some((f, idx) => idx !== index && f.key === newKey);
    if (isUsed) return; // Prevent duplicate keys
    
    const newFilters = [...state.filters];
    newFilters[index] = { key: newKey, value: "" }; // Reset value on key change
    onChange({ ...state, filters: newFilters });
  };

  const updateFilterValue = (index: number, newVal: FilterValue) => {
    const newFilters = [...state.filters];
    newFilters[index] = newVal;
    onChange({ ...state, filters: newFilters });
  };

  const removeFilter = (index: number) => {
    const newFilters = [...state.filters];
    newFilters.splice(index, 1);
    
    setFilterIds(prev => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });

    onChange({ ...state, filters: newFilters });
  };

  const updateSortKey = (index: number, newKey: string) => {
      // Allow changing to any key since we only have 1 row usually
      // But if we had multiple, we'd check duplicates. 
      // With max 1 sort, we just update.
      const newSorts = [...state.sorts];
      newSorts[index] = { ...newSorts[index], key: newKey };
      onChange({ ...state, sorts: newSorts });
  };

  const updateSortDirection = (index: number, direction: "asc" | "desc") => {
      const newSorts = [...state.sorts];
      newSorts[index] = { ...newSorts[index], direction };
      onChange({ ...state, sorts: newSorts });
  };
  
  const content = (
    <div className="flex flex-col bg-transparent max-h-[80vh] md:max-h-[600px] h-full overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100/80 bg-white/80 backdrop-blur-md sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 border border-blue-100 flex-shrink-0">
             <SlidersHorizontal className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="text-base font-bold text-gray-900">Anpassung</h3>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onReset}
          className="h-8 px-2.5 text-xs font-medium text-gray-500 hover:text-red-600 hover:bg-red-50/50 transition-colors rounded-lg"
        >
          <RotateCcw className="mr-1.5 h-3 w-3" />
          Zurücksetzen
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {/* Single Sort Section */}
        {state.sorts.length > 0 && (
          <div className="px-4 py-4 border-b border-gray-100/80 bg-transparent">
            <div className="flex items-center gap-3 justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-50 border border-gray-100 flex-shrink-0 text-gray-400">
                  <ArrowDownAZ className="h-4 w-4" />
                </div>
                <h4 className="text-sm font-bold text-gray-900">Sortierung</h4>
              </div>
              {sortCount > 0 && (
                <Badge variant="secondary" className="px-2 py-0.5 h-5 bg-gray-100 text-gray-600 border-none text-[10px] font-bold">
                  {sortCount}
                </Badge>
             )}
            </div>
            <AdvancedSortRow 
              sort={state.sorts[0]}
              sortDefs={sortDefs}
              availableKeys={sortDefs.map(d => d.key)} 
              onKeyChange={(newKey) => updateSortKey(0, newKey)}
              onDirectionChange={(dir) => updateSortDirection(0, dir)}
              onRemove={() => {}} 
              disableRemove={true}
            />
          </div>
        )}

        {/* Filter Section */}
         <div className="p-4 pb-12 md:pb-4 space-y-4 outline-none">
           <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-50 border border-gray-100 flex-shrink-0">
                   <Filter className="h-4 w-4 text-gray-400" />
                </div>
                <h4 className="text-sm font-bold text-gray-900">Filter</h4>
              </div>
              {filterCount > 0 && (
                <Badge variant="secondary" className="px-2 py-0.5 h-5 bg-gray-100 text-gray-600 border-none text-[10px] font-bold">
                  {filterCount}
                </Badge>
             )}
          </div>

          <div className="space-y-3">
            <AnimatePresence initial={false}>
              {state.filters.map((filter, idx) => (
                <motion.div
                  key={filterIds[idx]}
                  initial={{ opacity: 0, height: 0, y: -10 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -10, overflow: "hidden" }}
                  transition={{ duration: 0.2 }}
                >
                  <AdvancedFilterRow
                    filter={filter}
                    filterDefs={filterDefs}
                    availableKeys={filterDefs.map(d => d.key).filter(k => 
                      !state.filters.some((f, fIdx) => fIdx !== idx && f.key === k)
                    )}
                    onKeyChange={(newKey) => updateFilterKey(idx, newKey)}
                    onChange={(val) => updateFilterValue(idx, val)}
                    onRemove={() => removeFilter(idx)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full h-9 border-dashed border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50/30 transition-all rounded-lg text-xs font-medium"
            onClick={addFilter}
            disabled={state.filters.length >= filterDefs.length}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Filter hinzufügen
          </Button>
        </div>
      </div>
    </div>
  );

  const triggerButton = (
    <Button 
      variant="outline" 
      size="sm" 
      className={cn(
        "h-9 px-3 text-sm font-medium transition-all duration-200 rounded-xl border-gray-200",
        totalCount > 0 
           ? "bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100 hover:text-blue-800 shadow-sm" 
           : "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900",
        className
      )}
    >
      <SlidersHorizontal className={cn("h-4 w-4", totalCount > 0 ? "mr-2" : "mr-2")} />
      <span>Anpassung</span>
      {totalCount > 0 && (
        <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-blue-600 text-white border-none text-[10px] font-bold rounded-full">
          {totalCount}
        </Badge>
      )}
    </Button>
  );

  // We need to decide whether to render Popover or Sheet. 
  // Ideally this is responsive.
  // Tailwind 'md:hidden' utility approach is good for simple hiding.
  // But here we need to WRAP the content differently.
  
  if (isDesktop) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {triggerButton}
        </PopoverTrigger>
        <AnimatePresence>
          {isOpen && (
            <PopoverContent 
              forceMount
              asChild
              className="w-[420px] p-0 rounded-2xl shadow-xl border-gray-200/80 bg-white/95 backdrop-blur-xl flex flex-col overflow-hidden z-10 origin-top-right" 
              align="end"
              sideOffset={8}
              collisionPadding={16}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ 
                  default: {
                    type: "spring", 
                    stiffness: 350, 
                    damping: 35, 
                    mass: 0.8,
                  },
                  opacity: {
                    duration: 0.15,
                    ease: "easeOut"
                  }
                }}
              >
                {content}
              </motion.div>
            </PopoverContent>
          )}
        </AnimatePresence>
      </Popover>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          {triggerButton}
        </SheetTrigger>
        <SheetContent side="bottom" className="h-auto max-h-[85vh] rounded-t-3xl p-0 flex flex-col border-none shadow-2xl [&>button]:hidden">
          <SheetHeader className="sr-only">
             <SheetTitle>Anpassung</SheetTitle>
          </SheetHeader>
          {/* Mobile drag handle */}
          <div className="w-full flex justify-center pt-3 pb-1 bg-white absolute top-0 z-20 rounded-t-3xl">
            <div className="w-12 h-1.5 bg-gray-200 rounded-full" />
          </div>
          <div className="pt-6 overflow-hidden flex flex-col h-full bg-white rounded-t-3xl">
            {content}
          </div>
        </SheetContent>
    </Sheet>
  );
}
